import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import containerService from '../../services/ContainerService.js';
import userService from '../../services/UserService.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { Box, Plus, Filter, Eye, Pencil, Trash } from "lucide-react";

const ContainerList = () => {
  const [containers, setContainers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  // Get permissions from hook
  const { permissions } = usePermissions();
  const canCreate = permissions.containers.canCreate;
  const canEdit = permissions.containers.canEdit;
  const canDelete = permissions.containers.canDelete;

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchContainers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, clientFilter]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchContainers();
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const fetchContainers = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      const response = await containerService.getAll(currentPage, perPage, filters);
      setContainers(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load containers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (containerId, containerNo) => {
    if (!canDelete) {
      Alert.error('You do not have permission to delete containers');
      return;
    }
    
    const confirmed = await Alert.confirm(
      `Are you sure you want to permanently delete container "${containerNo}"? This action cannot be undone.`,
      'Delete Container'
    );
    if (confirmed) {
      try {
        await containerService.delete(containerId);
        Alert.success('Container deleted successfully!');
        if (containers.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchContainers();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete container');
      }
    }
  };

  const toggleDropdown = (containerId) => {
    setActiveDropdown(activeDropdown === containerId ? null : containerId);
  };

  const clearFilters = () => {
    setSearch('');
    setClientFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || clientFilter;

  const getClientName = (clientId) => {
    const client = clients.find(c => c.user_id === clientId);
    return client ? client.name : clientId;
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <Box className="w-5 h-5 text-buttonBg" />
                        Containers
                      </h1>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {canCreate && (
                        <button
                          onClick={() => navigate('/containers/add')}
                          className="
                                                      relative overflow-hidden
                                                      group flex items-center justify-center gap-2
                                                      px-3 sm:px-3 py-2.5 sm:py-2
                                                      text-xs sm:text-sm
                                                      rounded-lg
                                                      border border-buttonBg
                                                      bg-buttonBg text-buttonTxt
                                                      font-medium whitespace-nowrap
                          
                                                      transition-colors duration-300 ease-out
                                                      hover:bg-transparent hover:text-buttonBg
                                                    "
                                                  >
                                                    {/* COLLAPSING BACKGROUND */}
                                                    <span
                                                      className="
                                                        absolute inset-0
                                                        bg-buttonBg
                                                        scale-x-100
                                                        group-hover:scale-x-0
                                                        origin-center
                                                        transition-transform duration-300 ease-out
                                                        z-0
                                                      "
                                                    />
                          
                                                    {/* ICON */}
                                                    <Plus
                                                      className="
                                                        w-4 h-4 relative z-10
                                                        transition-transform duration-300 ease-out
                                                        group-hover:scale-90
                                                      "
                                                    />
                          
                                                    <span className="relative z-10">Add Container</span>
                          <span className="sm:hidden">Add</span>
                        </button>
                      )}
                    </div>
                  </div>

                {/* Filters */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                  </div>
                  <div className='hidden sm:block'>
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'> 
                          {/* Search */}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Search by Container No, Type, Manufacturer..."
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          </div>
                        

                          {/* Mobile Filter Toggle */}
                          <button
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <Filter size={18} />
                            Filters
                          </button>

                          {/* Desktop Filters */}
                          <div className="hidden lg:flex gap-3 flex-wrap">
                            <select
                              value={clientFilter}
                              onChange={(e) => setClientFilter(e.target.value)}
                              className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="">All Clients</option>
                              {clients.map((client) => (
                                <option key={client.user_id} value={client.user_id}>
                                  {client.name} {client.organization_name ? `(${client.organization_name})` : ''}
                                </option>
                              ))}
                            </select>

                            {hasActiveFilters && (
                              <button
                                onClick={clearFilters}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                              >
                                Clear Filters
                              </button>
                            )}
                          </div>
                        </div>
                  </div>

                  {/* Mobile Filters Dropdown */}
                  {showMobileFilters && (
                    <div className="lg:hidden mt-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Clients</option>
                        {clients.map((client) => (
                          <option key={client.user_id} value={client.user_id}>
                            {client.name} {client.organization_name ? `(${client.organization_name})` : ''}
                          </option>
                        ))}
                      </select>

                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="bg-white rounded-sm shadow-sm">
                  <div className="p-4 sm:p-6 lg:p-0">

                    {/* Table View - Desktop */}
                    <div className="hidden lg:block bg-white rounded-sm shadow-sm">
                      {loading ? (
                        <div className="p-12 text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading containers...
                        </div>
                      ) : containers.length === 0 ? (
                        <div className="p-12 text-center text-gray-600">No containers found</div>
                      ) : (
                        <>
                          <div className="hidden md:block w-full">
                                <table className="w-full table-fixed border-collapse">
                                  <thead>
                                    <tr className="bg-primary text-tertiary">
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Container No</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial No</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Radius</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Capacity (Ltr)</th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {containers.map((container) => (
                                  <tr key={container.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <button
                                        onClick={() => navigate(`/containers/view/${container.id}`)}
                                        className="text-sm font-medium text-gray-900 hover:underline"
                                      >
                                        {container.container_no || 'N/A'}
                                      </button>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-left text-gray-700">{getClientName(container.client_id)}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-left text-gray-700">{container.container_type || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-left text-gray-700">{container.manufacturer || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{container.serial_number || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{container.radius || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{container.capacity_ltr || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="relative group w-fit">
                                        <span className="text-sm text-gray-700 truncate max-w-[80px] inline-block">
                                          {container.created_by}
                                        </span>
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50 shadow-lg">
                                          {container.created_by}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                                      <button
                                        onClick={() => toggleDropdown(container.id)}
                                        className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                          <circle cx="12" cy="5" r="2"/>
                                          <circle cx="12" cy="12" r="2"/>
                                          <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                      </button>

                                      {activeDropdown === container.id && (
                                        <div
                                          ref={dropdownRef}
                                          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                        >
                                          <button
                                            onClick={() => { navigate(`/containers/view/${container.id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                          >
                                            <Eye size={16} className="text-buttonBg" />
                                            <span className="text-sm capitalize text-gray-600">View Details</span>
                                          </button>
                                          {canEdit && (
                                            <button
                                              onClick={() => { navigate(`/containers/edit/${container.id}`); setActiveDropdown(null); }}
                                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                            >
                                              <Pencil size={16} className="text-buttonBg" />
                                              <span className="text-sm capitalize text-gray-600">Edit</span>
                                            </button>
                                          )}
                                          {canDelete && (
                                            <button
                                              onClick={() => { handleDelete(container.id, container.container_no || 'this container'); setActiveDropdown(null); }}
                                              className="w-full px-4 py-2 text-left text-sm flex gap-2 items-center"
                                            >
                                              <Trash size={16} className="text-red-600" />
                                              <span className="text-sm capitalize text-gray-600">Delete</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                            <div className="text-sm text-gray-700">
                              Showing {containers.length} container{containers.length !== 1 ? 's' : ''}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Previous
                              </button>
                              <span className="px-4 py-2 text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="lg:hidden space-y-3 sm:space-y-4">
                      {loading ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading containers...
                        </div>
                      ) : containers.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No containers found</div>
                      ) : (
                        <>
                          {containers.map((container) => (
                            <div key={container.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                              <div className="flex items-start gap-3 sm:gap-4 mb-3">
                                <Box className="w-10 h-10 text-tertiary flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                                    {container.container_no || 'N/A'}
                                  </h3>
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                                    {getClientName(container.client_id)}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                                <div>
                                  <span className="text-gray-500">Type:</span>
                                  <p className="text-gray-900 font-medium">{container.container_type || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Manufacturer:</span>
                                  <p className="text-gray-900 font-medium">{container.manufacturer || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Serial No:</span>
                                  <p className="text-gray-900 font-medium">{container.serial_number || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Radius:</span>
                                  <p className="text-gray-900 font-medium">{container.radius || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Capacity (Ltr):</span>
                                  <p className="text-gray-900 font-medium">{container.capacity_ltr || 'N/A'}</p>
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => navigate(`/containers/view/${container.id}`)}
                                  className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center gap-1"
                                >
                                  <Eye size={14} /> View
                                </button>
                                {canEdit && (
                                  <button
                                    onClick={() => navigate(`/containers/edit/${container.id}`)}
                                    className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center gap-1"
                                  >
                                    <Pencil size={14} /> Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDelete(container.id, container.container_no || 'this container')}
                                    className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                                  >
                                    <Trash size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Mobile Pagination */}
                          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                            <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                              Showing {containers.length} container{containers.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainerList;
